import React, { useEffect, useState } from "react";
import api from "../lib/api";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { TrendingUp, Clock, Gavel } from "lucide-react";

const CHART_COLORS = ["#B33A3A", "#D9A05B", "#0F172A", "#64748B", "#94A3B8", "#F87171", "#CBD5E1"];

function fmtUSD(n) {
  if (!n) return "$0";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
}

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/analytics/overview");
        setData(r.data);
      } catch {}
    })();
  }, []);

  if (!data) return <div className="p-8 eyebrow text-slate-500">Loading analytics…</div>;

  return (
    <div className="p-6 md:p-8 fade-in-up" data-testid="analytics-page">
      <div className="mb-8">
        <div className="eyebrow">Analytics & Reports</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight mt-1">
          Portfolio Intelligence
        </h1>
        <p className="text-slate-600 mt-2 text-sm md:text-base">
          Deep metrics across value, geography, filings, renewals, and marketplace performance.
        </p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
        <div className="bengal-card p-5">
          <div className="eyebrow">Total Portfolio Value</div>
          <div className="font-mono text-3xl text-[#B33A3A] font-semibold mt-2">{fmtUSD(data.total_value_usd)}</div>
        </div>
        <div className="bengal-card p-5">
          <div className="eyebrow flex items-center gap-1"><Gavel size={11}/> Listings Open</div>
          <div className="font-mono text-3xl font-semibold mt-2">{data.marketplace.listings_open}</div>
        </div>
        <div className="bengal-card p-5">
          <div className="eyebrow flex items-center gap-1"><TrendingUp size={11}/> Deals Closed</div>
          <div className="font-mono text-3xl font-semibold mt-2">{data.marketplace.listings_closed}</div>
        </div>
        <div className="bengal-card p-5">
          <div className="eyebrow flex items-center gap-1"><Clock size={11}/> Bids Placed</div>
          <div className="font-mono text-3xl font-semibold mt-2">{data.marketplace.bids_placed}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {/* Value by country */}
        <div className="bengal-card p-6" data-testid="value-by-country">
          <div className="eyebrow">Portfolio Value by Country</div>
          <h3 className="font-serif text-2xl font-medium mt-1 mb-4">Where the value lives</h3>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={data.value_by_country.slice(0, 10)} layout="vertical" margin={{ left: 10, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" tickFormatter={(v) => fmtUSD(v)} tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="country" tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} width={45} />
                <Tooltip formatter={(v) => fmtUSD(v)} contentStyle={{ borderRadius: 2, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Bar dataKey="value" fill="#B33A3A" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Filings per year */}
        <div className="bengal-card p-6" data-testid="filings-per-year">
          <div className="eyebrow">Filings Timeline</div>
          <h3 className="font-serif text-2xl font-medium mt-1 mb-4">Year over year</h3>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={data.filings_per_year}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Line type="monotone" dataKey="count" stroke="#B33A3A" strokeWidth={2} dot={{ fill: "#B33A3A", r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Value by category */}
        <div className="bengal-card p-6" data-testid="value-by-category">
          <div className="eyebrow">Value by Domain</div>
          <h3 className="font-serif text-2xl font-medium mt-1 mb-4">Categorical breakdown</h3>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={data.value_by_category}
                  dataKey="value"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={95}
                  innerRadius={50}
                  paddingAngle={2}
                  stroke="#fff"
                  strokeWidth={2}
                >
                  {data.value_by_category.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmtUSD(v)} contentStyle={{ borderRadius: 2, border: "1px solid #E2E8F0", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Renewal timeline */}
        <div className="bengal-card p-6" data-testid="renewal-timeline">
          <div className="eyebrow">Upcoming Renewals</div>
          <h3 className="font-serif text-2xl font-medium mt-1 mb-4">Next 24 months</h3>
          {data.renewal_timeline.length === 0 ? (
            <div className="text-sm text-slate-500 py-10 text-center">No renewals scheduled.</div>
          ) : (
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={data.renewal_timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#475569" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar dataKey="count" fill="#D9A05B" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
